"use client";

import { useState, useEffect } from "react";
import { MoveRight } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import GradientText from "@/components/GradientText";
import { CATALOG_PDF_PATH } from "@/lib/constants";

import MobileCategoryNav from "./MobileCategoryNav";

const NAV_LINKS = [
  { name: "خانه", href: "/" },
  { name: "محصولات", href: "/product" },
  { name: "وبلاگ", href: "/blog" },
  { name: "برندها", href: "/brands" },
  { name: "درباره ما", href: "/about-us" },
  { name: "کاتالوگ", href: CATALOG_PDF_PATH, download: true },
  { name: "فروشگاه ها", href: "/stores" },
];

function isLinkActive(pathname, href) {
  if (!href || href.startsWith("http")) return false;
  if (href === "/") return pathname === "/";
  return pathname === href || pathname.startsWith(`${href}/`);
}

export default function MobileNav({ isOpen, onClose }) {
  const pathname = usePathname();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      document.body.style.overflow = "hidden";
    } else {
      const timer = setTimeout(() => setMounted(false), 300);
      document.body.style.overflow = "";
      return () => clearTimeout(timer);
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!mounted && !isOpen) return null;

  return (
    <div
      className={cn(
        "fixed inset-0 z-[100] flex flex-col overflow-hidden bg-[#080808] font-dana transition-all duration-300 ease-in-out",
        isOpen ? "opacity-100 translate-x-0" : "pointer-events-none opacity-0 translate-x-full"
      )}
      aria-hidden={!isOpen}
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="absolute top-[65%] left-1/2 h-[600px] w-full -translate-x-1/2 -translate-y-1/2 opacity-30 blur-[120px]"
          style={{
            background: "radial-gradient(circle, #f43f5e 0%, transparent 70%)",
          }}
        />
      </div>

      <div className="relative flex shrink-0 items-center justify-between px-4 py-5 sm:px-6">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onClose}
            className="group flex items-center justify-center transition-transform hover:scale-110 active:scale-95"
            aria-label="بستن منو"
          >
            <div className="relative h-8 w-8">
              <div className="absolute top-1/2 left-0 h-[2.5px] w-full rotate-45 rounded-full bg-berry-500 shadow-[0_0_10px_rgba(244,63,94,0.8)]" />
              <div className="absolute top-1/2 left-0 h-[2.5px] w-full -rotate-45 rounded-full bg-berry-500 shadow-[0_0_10px_rgba(244,63,94,0.8)]" />
            </div>
          </button>
          <div className="mx-1 h-10 w-px bg-white/20" />
          <span className="text-base font-medium text-white sm:text-lg">منو</span>
        </div>
      </div>

      <nav
        className="relative flex-1 overflow-y-auto overflow-x-hidden px-4 pb-8 sm:px-6 custom-scrollbar-dark"
        aria-label="منوی موبایل"
      >
        <section className="mb-8">
          <MobileCategoryNav onClose={onClose} />
        </section>

        <div className="mb-4 h-px w-full bg-white/10" />

        <section>
          <p className="mb-3 px-1 text-base font-medium text-white">صفحات</p>
          <ul className="flex flex-col gap-1">
            {NAV_LINKS.map((link) => {
              const active = isLinkActive(pathname, link.href);
              const isExternal = link.href?.startsWith("http");
              const useAnchor = isExternal || link.download;

              const linkContent = (
                <>
                  <span
                    className={cn(
                      "text-sm font-medium sm:text-base",
                      active && link.name === "محصولات" ? "" : active ? "text-berry-500" : "text-white"
                    )}
                  >
                    {active && link.name === "محصولات" ? (
                      <GradientText className="text-sm sm:text-base">محصولات</GradientText>
                    ) : (
                      link.name
                    )}
                  </span>
                  <MoveRight
                    className={cn(
                      "h-5 w-5 shrink-0 sm:h-6 sm:w-6",
                      active ? "text-berry-500" : "text-white"
                    )}
                  />
                </>
              );

              const linkClass = cn(
                "flex w-full items-center justify-between gap-3 rounded-2xl px-4 py-3.5 transition-all",
                active ? "bg-white/10" : "hover:bg-white/5"
              );

              return (
                <li key={link.name}>
                  {useAnchor ? (
                    <a
                      href={link.href}
                      download={link.download ? "catalog.pdf" : undefined}
                      onClick={onClose}
                      className={linkClass}
                      target={isExternal && !link.download ? "_blank" : undefined}
                      rel={isExternal && !link.download ? "noopener noreferrer" : undefined}
                    >
                      {linkContent}
                    </a>
                  ) : (
                    <Link href={link.href} onClick={onClose} className={linkClass}>
                      {linkContent}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      </nav>
    </div>
  );
}
